import type { ReactNode } from 'react';
import styles from './DocLayout.module.css';
import { cx } from '../../lib/cx';

export type DocStatus = 'draft' | 'done';

export interface DocLayoutProps {
  breadcrumb: ReactNode[];
  title: ReactNode;
  status?: DocStatus;
  statusText?: string;
  actions?: ReactNode;
  /** Volitelný progress (např. „72 % vyplněno"). Vykreslí se jako pill v headeru. */
  formProgress?: { percent: number; label?: ReactNode; sub?: ReactNode };
  /** Obsah dokumentu — typicky `<DocPage>...</DocPage>`. */
  children?: ReactNode;
  /** @deprecated Backward-compat pro nemigrované stránky. Použij `children`. */
  preview?: ReactNode;
  /** @deprecated Backward-compat. V „živém dokumentu" už není formulářová hlavička. */
  formHeader?: ReactNode;
}

/**
 * Single-column "živý dokument" layout.
 * Header (breadcrumb / title / status / akce) + scrollable canvas s dokumentem.
 * Dokument samotný je editovatelný — žádný oddělený formulář.
 */
export function DocLayout({
  breadcrumb,
  title,
  status = 'draft',
  statusText = 'Rozpracovaný koncept',
  actions,
  formProgress,
  children,
  preview,
}: DocLayoutProps) {
  // Backward-compat: starší stránky stále posílají `preview={...}` jako
  // pravý sloupec a `children` jako formulář vlevo. V novém single-column
  // layoutu vykreslíme jen samotný dokument (preview má přednost).
  const docContent = preview ?? children;
  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          <div className={styles.breadcrumb}>
            {breadcrumb.flatMap((item, i) =>
              i === 0
                ? [<span key={i}>{item}</span>]
                : [<span key={`s${i}`} className={styles.sep}>/</span>, <span key={i}>{item}</span>],
            )}
          </div>
          <h1 className={styles.title}>{title}</h1>
        </div>

        {formProgress && (
          <div className={styles.progressPill} title={`${formProgress.percent} % vyplněno`}>
            <span className={styles.progressLabel}>{formProgress.label ?? 'Vyplněno'}</span>
            <span className={styles.progressBar}>
              <span
                className={styles.progressBarFill}
                style={{ width: `${Math.max(0, Math.min(100, formProgress.percent))}%` }}
              />
            </span>
            <span className={styles.progressPct}>{formProgress.percent} %</span>
          </div>
        )}

        <div className={cx(styles.statusBadge, status === 'done' && styles.done)}>
          <span className={styles.dot} aria-hidden />
          {statusText}
        </div>

        {actions && <div className={styles.headerActions}>{actions}</div>}
      </header>

      <div className={styles.body}>
        <div className={styles.bodyInner}>{docContent}</div>
      </div>
    </div>
  );
}
